/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';

// Types
import { TApiResponseGetAco2Tokens } from '@global-types/api/api';

// Lib
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import {
	QueryParams,
	RangeFilter,
} from '@features/catalogs/meausrability-projects/list/components/Filter/Filter';
import { TAco2Token } from '@features/sourcing/mint-history/types';
import {
	convertGCO2EToTCO2E,
	convertTCO2EToGCO2EWithoutUnit,
	formatDate,
} from '@utils/functions';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';

export function normalizeQueryParams(
	restQueryParams: QueryParams
): QueryParams {
	const path = 'quantity.remaining_aco2';

	const range = get(restQueryParams, path) as
		| RangeFilter
		| undefined;

	if (!range) {
		return restQueryParams;
	}

	const updatedRange = mapValues(range, (value, key) => {
		if (
			typeof value === 'number' &&
			(key === '$gte' || key === '$lte')
		) {
			return convertTCO2EToGCO2EWithoutUnit(value);
		}
		return value;
	});

	return {
		...restQueryParams,
		[path]: updatedRange,
	};
}

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
type TParamsGetApi = {
	page: number;
	pageSize: number;
	populate?: Array<string> | string;
	query: QueryParams;
	sortParams: string | null;
	status: string;
	project?: string;
	sort?: string;
};

export const getAco2Tokens = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetAco2Tokens> => {
	const { query, page, pageSize, sort, project } = params;

	return apiClient.get('/aco2_tokens', {
		params: {
			...(query
				? { query: normalizeQueryParams(query) }
				: {}),
			page,
			pageSize,
			sort,
			project,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TUseGetAco2TokensOptions = {
	params: Partial<TParamsGetApi>;
	queryConfig?: QueryConfig<typeof getAco2Tokens>;
};

/* -------------------------------------------------------------------------- */
/*                                    Mapper                                  */
/* -------------------------------------------------------------------------- */
export const mapApiAco2Token = (
	apiAco2Token: TAco2Token
): {
	id: string;
	createdAt: string;
	startDate: string;
	endDate: string;
	quantityTco2e: string;
} => ({
	id: apiAco2Token.id,
	createdAt: formatDate(
		apiAco2Token.createdAt,
		'DD/MM/YYYY'
	),
	startDate: formatDate(
		apiAco2Token?.startDate,
		'DD/MM/YYYY'
	),
	endDate: formatDate(
		apiAco2Token?.endDate,
		'DD/MM/YYYY'
	),
	quantityTco2e: convertGCO2EToTCO2E(
		apiAco2Token?.quantity?.total_aco2
	),
});

export const mapApiAco2TokenResponse = (
	apiResponse: TApiResponseGetAco2Tokens
): Omit<TApiResponseGetAco2Tokens, 'rows'> & {
	rows: {
		id: string;
		createdAt: string;
		startDate: string;
		endDate: string;
		quantityTco2e: string;
	}[];
} => ({
	rows: apiResponse.rows.map(mapApiAco2Token),
	total: apiResponse.total,
	page: apiResponse.page,
	pageSize: apiResponse.pageSize,
	totalPages: apiResponse.totalPages,
});

/* -------------------------------------------------------------------------- */
/*                                    Helper                                  */
/* -------------------------------------------------------------------------- */
export const getAco2TokensQueryOptions = (
	params: Partial<TParamsGetApi> = {}
) => {
	return queryOptions({
		queryKey: [
			queryKeys.app.aco2Token.listByProject.key,
			params,
		],
		queryFn: () => getAco2Tokens(params),
		select: mapApiAco2TokenResponse,
		staleTime: 0,
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useGetAco2Tokens = ({
	params,
	queryConfig,
}: TUseGetAco2TokensOptions) => {
	return useQuery({
		...getAco2TokensQueryOptions(params),
		...queryConfig,
	});
};
